<template>
  <div class="date-picker-container relative w-full mb-8 max-w-md">
    <!-- Input Fields Container -->
    <div class="flex border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <!-- Check-in Field -->
      <DateInputField
        :label="$t('testfleet.booking.customPeriodDatePicker.pickup')"
        :date="pickupDate"
        :placeholder="$t('testfleet.booking.customPeriodDatePicker.addDates')"
        :is-active="showPickupCalendar"
        icon="CalendarToday"
        @click="openPickupCalendar"
      />

      <!-- Check-out Field -->
      <DateInputField
        :label="$t('testfleet.booking.customPeriodDatePicker.dropoff')"
        :date="dropoffDate"
        :placeholder="$t('testfleet.booking.customPeriodDatePicker.addDates')"
        :is-active="showDropoffCalendar"
        :disabled="!pickupDate"
        icon="ArrowDown"
        @click="() => pickupDate ? openDropoffCalendar() : null"
      />
    </div>

    <!-- Pickup Calendar Dropdown -->
    <CalendarDropdown
      :is-visible="showPickupCalendar"
      :title="$t('testfleet.booking.customPeriodDatePicker.selectPickupDate')"
      v-model="pickupDate"
      :min-date="new Date(availabilities.start_date)"
      :max-date="new Date(availabilities.end_date)"
      :disabled-dates="disabledPickupDates"
      @close="closePickupCalendar"
    />

    <!-- Dropoff Calendar Dropdown -->
    <CalendarDropdown
      :is-visible="showDropoffCalendar"
      :title="$t('testfleet.booking.customPeriodDatePicker.selectDropoffDate')"
      v-model="dropoffDate"
      :min-date="minDropoffDate"
      :max-date="maxDropoffDate"
      :disabled-dates="disabledDropoffDates"
      @close="closeDropoffCalendar"
    />
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import { Availability } from "@/services/bikes/types"

// Import composables
import { useDateRange, useCalendarState, useDateRangeEvents } from './composables'

// Import components
import { DateInputField, CalendarDropdown } from './components'

const props = defineProps({
  availabilities: {
    type: Object as PropType<Availability>,
    required: true,
  },
})

// Use composables
const {
  pickupDate,
  dropoffDate,
  minDropoffDate,
  maxDropoffDate,
  disabledPickupDates,
  disabledDropoffDates,
} = useDateRange(props.availabilities)

const {
  showPickupCalendar,
  showDropoffCalendar,
  openPickupCalendar,
  openDropoffCalendar,
  closePickupCalendar,
  closeDropoffCalendar,
} = useCalendarState()

// Define emits
const emit = defineEmits<{
  pickupDateSelected: [Date]
  dropoffDateSelected: [Date]
}>()

// Setup event handling
useDateRangeEvents(
  pickupDate,
  dropoffDate,
  openDropoffCalendar,
  closeDropoffCalendar,
  {
    pickupDateSelected: (date) => emit('pickupDateSelected', date),
    dropoffDateSelected: (date) => emit('dropoffDateSelected', date)
  }
)
</script>

<style scoped>
.vc-prev, .vc-next {
  display: block !important;
}
</style>